/**
 * Utility classes and functions for the Swimmable SDK
 */

import { Coordinates } from './types';

/**
 * Custom error class for Swimmable API errors
 */
export class SwimmableError extends Error {
  public status?: number;
  public code?: string;
  public endpoint?: string;

  constructor(message: string, status?: number, code?: string, endpoint?: string) {
    super(message);
    this.name = 'SwimmableError';
    this.status = status;
    this.code = code;
    this.endpoint = endpoint;
  }
}

/**
 * Utility functions for working with coordinates and locations
 */
export class LocationUtils {
  /**
   * Validate that coordinates are within valid ranges
   * @param coordinates - Coordinates to validate
   */
  static validateCoordinates(coordinates: Coordinates): boolean {
    const { lat, lon } = coordinates;
    return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
  }

  /**
   * Calculate the distance between two coordinates using the Haversine formula
   * @param coord1 - First coordinate
   * @param coord2 - Second coordinate
   * @returns Distance in kilometers
   */
  static calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(coord2.lat - coord1.lat);
    const dLon = this.toRadians(coord2.lon - coord1.lon);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(coord1.lat)) * Math.cos(this.toRadians(coord2.lat)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param degrees - Degrees to convert
   */
  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Find the nearest spot from a list of coordinates
   * @param target - Target coordinates
   * @param spots - Array of spots with coordinates
   */
  static findNearestSpot<T extends Coordinates>(target: Coordinates, spots: T[]): T | null {
    if (spots.length === 0) return null;

    let nearest = spots[0];
    let minDistance = this.calculateDistance(target, nearest);

    for (let i = 1; i < spots.length; i++) {
      const distance = this.calculateDistance(target, spots[i]);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = spots[i];
      }
    }

    return nearest;
  }
}

/**
 * Utility functions for working with swimming conditions
 */
export class ConditionsUtils {
  /**
   * Convert temperature from Celsius to Fahrenheit
   * @param celsius - Temperature in Celsius
   */
  static celsiusToFahrenheit(celsius: number): number {
    return (celsius * 9/5) + 32;
  }

  /**
   * Convert temperature from Fahrenheit to Celsius
   * @param fahrenheit - Temperature in Fahrenheit
   */
  static fahrenheitToCelsius(fahrenheit: number): number {
    return (fahrenheit - 32) * 5/9;
  }

  /**
   * Get a human-readable description of the swimmability score
   * @param score - Swimmability score (1-10)
   */
  static getSwimmabilityDescription(score: number): string {
    if (score >= 9) return 'Excellent swimming conditions';
    if (score >= 7) return 'Good swimming conditions';
    if (score >= 5) return 'Fair swimming conditions';
    if (score >= 3) return 'Poor swimming conditions';
    return 'Dangerous swimming conditions';
  }

  /**
   * Get a color code for the swimmability score (useful for UI)
   * @param score - Swimmability score (1-10)
   */
  static getSwimmabilityColor(score: number): string {
    if (score >= 8) return '#10B981'; // Green
    if (score >= 6) return '#F59E0B'; // Yellow
    if (score >= 4) return '#F97316'; // Orange
    return '#EF4444'; // Red
  }

  /**
   * Check if conditions are safe for swimming based on various factors
   * @param conditions - Enhanced conditions object
   */
  static isSafeForSwimming(conditions: any): boolean {
    // Basic safety checks
    if (conditions.swimmabilityScore < 5) return false;
    if (conditions.warnings && conditions.warnings.length > 0) return false;
    
    // Check water temperature (too cold can be dangerous)
    const waterTemp = conditions.conditions?.water?.temperature?.value;
    if (waterTemp && waterTemp < 15) return false; // Below 15°C is generally too cold
    
    return true;
  }
}

/**
 * Utility functions for API key management
 */
export class ApiKeyUtils {
  /**
   * Validate API key format
   * @param apiKey - API key to validate
   */
  static validateApiKey(apiKey: string): boolean {
    // Swimmable API keys start with 'swm_' followed by 64 hex characters
    return /^swm_[a-f0-9]{64}$/.test(apiKey);
  }

  /**
   * Get the prefix of an API key (for display purposes)
   * @param apiKey - Full API key
   */
  static getKeyPrefix(apiKey: string): string {
    if (apiKey.length < 12) return apiKey;
    return `${apiKey.substring(0, 12)}...`;
  }

  /**
   * Mask an API key for secure display
   * @param apiKey - API key to mask
   */
  static maskApiKey(apiKey: string): string {
    if (apiKey.length < 8) return '***';
    return `${apiKey.substring(0, 4)}${'*'.repeat(apiKey.length - 8)}${apiKey.substring(apiKey.length - 4)}`;
  }
}

/**
 * Rate limiting utilities for client-side throttling
 */
export class RateLimiter {
  private requests: number[] = [];
  private maxRequests: number;
  private windowMs: number;

  constructor(maxRequests: number = 100, windowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  /**
   * Check if a request can be made without exceeding rate limits
   */
  canMakeRequest(): boolean {
    const now = Date.now();
    
    // Remove requests outside the current window
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    
    return this.requests.length < this.maxRequests;
  }

  /**
   * Record a request (call this after making a successful request)
   */
  recordRequest(): void {
    this.requests.push(Date.now());
  }

  /**
   * Get the number of requests remaining in the current window
   */
  getRemainingRequests(): number {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxRequests - this.requests.length);
  }

  /**
   * Get the time until the rate limit window resets (in milliseconds)
   */
  getResetTime(): number {
    if (this.requests.length === 0) return 0;
    
    const oldestRequest = Math.min(...this.requests);
    const resetTime = oldestRequest + this.windowMs;
    return Math.max(0, resetTime - Date.now());
  }
}